<template>
    <div class="keyboard-access-control" v-if="isTutor">
        <b-dropdown 
            size="sm" 
            variant="link" 
            toggle-class="text-decoration-none" 
            no-caret
            class="access-dropdown"
        >
            <template #button-content>
                <div class="d-flex align-items-center">
                    <img src="@/assets/icons/academyIcons/39.png" alt="Access Control" width="24" class="me-2">
                    <span class="access-text">{{ getAccessText }}</span>
                </div>
            </template>
            <b-dropdown-item @click="clearAccess">
                <div class="d-flex align-items-center">
                    <i class="fas fa-lock me-2"></i>
                    <span>{{ $t('academy.no_access') }}</span>
                </div>
            </b-dropdown-item>
            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item 
                v-for="student in students" 
                :key="student.uid"
                @click="grantAccess(student)"
                :class="{ 'active': isStudentSelected(student.uid) }"
            >
                <div class="d-flex align-items-center">
                    <i class="fas fa-user me-2"></i>
                    <span>{{ student.name }}</span>
                </div>
            </b-dropdown-item>
        </b-dropdown>
    </div>
</template>

<script>
import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '@/data/firebaseConfig';
import { getAuth } from 'firebase/auth';

export default {
    name: 'KeyboardAccessControl',
    props: {
        groupId: {
            type: String,
            required: true
        },
        students: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            isTutor: false,
            currentAccess: null
        };
    },
    computed: {
        getAccessText() {
            if (!this.currentAccess) return 'Sin acceso';
            const student = this.students.find(s => s.uid === this.currentAccess.studentUid);
            return student ? `Acceso: ${student.name}` : 'Sin acceso';
        }
    },
    methods: {
        async checkUserRole() {
            const auth = getAuth();
            const user = auth.currentUser;
            if (!user) return;

            try {
                const groupRef = doc(db, 'groups', this.groupId);
                const groupDoc = await getDoc(groupRef);
                
                if (groupDoc.exists()) {
                    const groupData = groupDoc.data();
                    this.isTutor = groupData.mentor?.uid === user.uid;
                    
                    // Cargar el acceso actual
                    if (groupData.keyboardAccess) {
                        this.currentAccess = groupData.keyboardAccess;
                    }
                }
            } catch (error) {
                console.error('Error al verificar rol:', error);
            }
        },
        async grantAccess(student) {
            try {
                const groupRef = doc(db, 'groups', this.groupId);
                const groupDoc = await getDoc(groupRef);
                
                if (groupDoc.exists()) {
                    const groupData = groupDoc.data();
                    const currentActivity = groupData.activities?.find(
                        act => act.studentUid === student.uid
                    );

                    await updateDoc(groupRef, {
                        keyboardAccess: {
                            studentUid: student.uid,
                            activityId: currentActivity?.projectUid || null,
                            grantedAt: new Date().toISOString()
                        }
                    });

                    this.currentAccess = {
                        studentUid: student.uid,
                        activityId: currentActivity?.projectUid || null
                    };

                    this.$bvToast.toast(`Acceso concedido a ${student.name}`, {
                        title: this.$t('academy.success'),
                        variant: 'success',
                        solid: true
                    });
                }
            } catch (error) {
                console.error('Error al conceder acceso:', error);
                this.$bvToast.toast(this.$t('academy.grant_access_error'), {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            }
        },
        async clearAccess() {
            try {
                const groupRef = doc(db, 'groups', this.groupId);
                await updateDoc(groupRef, {
                    keyboardAccess: null
                });

                this.currentAccess = null;

                this.$bvToast.toast(this.$t('academy.access_revoked'), {
                    title: this.$t('academy.success'),
                    variant: 'success',
                    solid: true
                });
            } catch (error) {
                console.error('Error al revocar acceso:', error);
                this.$bvToast.toast(this.$t('academy.revoke_access_error'), {
                    title: 'Error',
                    variant: 'danger',
                    solid: true
                });
            }
        },
        isStudentSelected(studentUid) {
            return this.currentAccess?.studentUid === studentUid;
        }
    },
    mounted() {
        this.checkUserRole();
    },
    watch: {
        groupId: {
            immediate: true,
            handler() {
                this.checkUserRole();
            }
        }
    }
};
</script>

<style scoped>
.keyboard-access-control {
    display: inline-flex;
    align-items: center;
}

.access-dropdown {
    padding: 0.5rem;
    border-radius: 4px;
    transition: all 0.3s ease;
}

.access-dropdown:hover {
    background-color: rgba(118, 184, 241, 0.1);
}

.access-text {
    font-size: 0.9rem;
    color: #232323;
    font-weight: 500;
}

::v-deep .dropdown-menu {
    min-width: 200px;
    padding: 0.5rem 0;
    margin: 0;
    border: 1px solid rgba(0,0,0,.15);
    border-radius: 0.25rem;
    box-shadow: 0 0.5rem 1rem rgba(0,0,0,.175);
}

::v-deep .dropdown-item {
    padding: 0.5rem 1rem;
    display: flex;
    align-items: center;
    transition: all 0.2s ease;
}

::v-deep .dropdown-item:hover {
    background-color: #f8f9fa;
    transform: translateX(5px);
}

::v-deep .dropdown-item.active {
    background-color: #76b8f1;
    color: white;
}

::v-deep .dropdown-divider {
    margin: 0.5rem 0;
    opacity: 0.1;
}
</style> 